import type { ElementType } from 'react'
import { useEffect, useRef } from 'react'

import { IconLink, IconTicket } from '@tabler/icons-react'
import confetti from 'canvas-confetti'

import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogDismiss,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { trackAffiliateWelcomeDialogViewed } from '@/lib/analytics/affiliate-tracking'

import locales from './locales'

interface Step {
  key: string
  icon: ElementType
  color: string
  background: string
}

const STEPS: ReadonlyArray<Step> = [
  {
    key: 'link',
    icon: IconLink,
    color: 'text-blue-600 dark:text-blue-400',
    background: 'bg-blue-500/10',
  },
  {
    key: 'coupon',
    icon: IconTicket,
    color: 'text-violet-600 dark:text-violet-400',
    background: 'bg-violet-500/10',
  },
]

interface AffiliateWelcomeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Shown once, right after activation. It celebrates and explains — it does not
 * branch.
 *
 * The two steps used to carry a button each, beside the footer's own: three
 * actions for a choice that only ever admits one, and two of them navigating
 * away from a dialog the affiliate had just earned. Both pages are one click
 * away in the submenu and carry their own create button, so the steps are read
 * here and acted on there.
 */
export function AffiliateWelcomeDialog({
  open,
  onOpenChange,
}: AffiliateWelcomeDialogProps) {
  const { t } = useComponentTranslation(locales)

  const hasCelebrated = useRef(false)

  useEffect(() => {
    if (open && !hasCelebrated.current) {
      hasCelebrated.current = true
      trackAffiliateWelcomeDialogViewed()
      // Same celebration the subscription activation fires, and behind the
      // same guard — StrictMode double-invokes effects in dev, and a reader
      // who asked the OS for less motion gets the dialog without it.
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          zIndex: 9999,
        })
      }
    }
    if (!open) hasCelebrated.current = false
  }, [open])

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{t('title')}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {t('description')}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <ResponsiveDialogBody>
          {/* No action per row: the text now has the whole width it was wrapping one word at a time without. */}
          <ol className="space-y-3">
            {STEPS.map((step, index) => (
              <li key={step.key} className="flex items-start gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${step.background}`}
                >
                  <step.icon className={`h-5 w-5 ${step.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {index + 1}. {t(`steps.${step.key}.title`)}
                  </p>
                  <p className="text-muted-foreground mt-1.5 text-xs">
                    {t(`steps.${step.key}.description`)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </ResponsiveDialogBody>

        <ResponsiveDialogFooter>
          <ResponsiveDialogDismiss intent="acknowledge">
            {t('actions.start')}
          </ResponsiveDialogDismiss>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
